import React from "react";
import PropTypes from "prop-types";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  UpcomingSubscriptionText,
} from "./styles";
import { useUpcomingSubscriptionChanges } from "../../../../services/queries";
import { useCancelScheduledSubscriptionChange } from "../../../../services/mutations";
import { Box, Button } from "@mui/material";
import { toast } from "react-toastify";

/**
 * Component displaying information about an upcoming subscription change
 * Self-contained component that fetches its own data
 */
const UpcomingSubscriptionBoxComponent = () => {
  const { data, isLoading, error, refetch } = useUpcomingSubscriptionChanges();
  const cancelScheduledChange = useCancelScheduledSubscriptionChange();
  
  // If loading or error or no changes scheduled, don't render anything
  if (isLoading || error || !data || !data.has_scheduled_changes) {
    return null;
  }
  
  const currentSubscription = data.current_subscription;
  const newPackage = data.new_package;
  
  // Safely handle potentially missing properties
  if (!currentSubscription || !currentSubscription.name || !newPackage || !newPackage.name) {
    return null;
  }
  
  // Handle different change types
  const isDowngrade = data.change_type === "downgrade";
  const isCancellation = data.change_type === "cancellation";
  
  // Determine billing cycle (we might need to extend the API to include this information)
  const billingCycle = "monthly"; // Default to monthly if not provided
  const formattedBillingCycle = billingCycle === "annual" ? "Annually" : "Monthly";
  
  // Construct the appropriate message based on change type
  let message = "";
  if (isDowngrade) {
    message = `Your subscription will change to ${newPackage.name} (${formattedBillingCycle}) on ${data.effective_date}.`;
  } else if (isCancellation) {
    message = `Your subscription will change to ${newPackage.name} on ${data.effective_date}.`;
  }
  
  const handleKeepCurrentPlan = async () => {
    try {
      const response = await cancelScheduledChange.mutateAsync();
      // Refresh the subscription changes data after successful cancellation
      refetch();
      
      // Show success toast message
      if (response?.data?.message) {
        toast.success(response.data.message);
      } else {
        toast.success(`Your scheduled change to ${newPackage.name} has been cancelled successfully.`);
      }
    } catch (error) {
      console.error("Failed to cancel scheduled change:", error);
      toast.error(error?.response?.data?.message || "Failed to cancel scheduled change. Please try again.");
    }
  };
  
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        padding: "16px",
        background: "rgba(247, 154, 0, 0.1)",
        border: "1px solid #F79A00",
        borderRadius: "12px",
        width: "fit-content",
        mt:2
      }}
    >
      <WarningAmberIcon
      sx={{mr:1}}
        style={{ color: "#E87033", width: "24px", height: "24px" }}
      />
      <UpcomingSubscriptionText>
        {message}
        <Button
          variant="text"
          style={{
            marginLeft: "8px",
            padding: "0",
            fontWeight: "600",
            color: "#9B5AF8",
            minWidth: "auto",
            textTransform: "none",
            textDecoration: "underline",
          }}
          onClick={handleKeepCurrentPlan}
          disabled={cancelScheduledChange.isLoading}
        >
          Keep {currentSubscription.name}
        </Button>
      </UpcomingSubscriptionText>
    </Box>
  );
};

export default UpcomingSubscriptionBoxComponent; 